import { useEffect, useState } from 'react';

export interface ClosetCarouselItem {
  id: number;
  user_id: number;
  name: string;
  category: string;
  photos: string[];
  description: string;
  marque: string;
  couleur: string;
  taille: string;
  occasion: string;
  created_at: string;
  updated_at: string;
}

interface ClosetCarouselProps {
  /** Libellé de la partie (Haut, Milieu, Bas…). */
  title: string;
  /** Icône Remix de la partie, utilisée en repli quand il n'y a pas de photo. */
  icon: string;
  items: ClosetCarouselItem[];
  emptyLabel: string;
  onOpen: (item: ClosetCarouselItem) => void;
  onDelete: (item: ClosetCarouselItem) => void;
  /** Ajout rapide d'une pièce directement dans cette partie. */
  onQuickAdd?: () => void;
}

const CLOSET_COLORS: { name: string; hex: string }[] = [
  { name: 'Noir', hex: '#1F2937' },
  { name: 'Blanc', hex: '#F9FAFB' },
  { name: 'Gris', hex: '#9CA3AF' },
  { name: 'Beige', hex: '#D8C3A5' },
  { name: 'Marron', hex: '#8B5E3C' },
  { name: 'Rouge', hex: '#DC2626' },
  { name: 'Rose', hex: '#EC4899' },
  { name: 'Orange', hex: '#F97316' },
  { name: 'Jaune', hex: '#FACC15' },
  { name: 'Vert', hex: '#16A34A' },
  { name: 'Bleu', hex: '#2563EB' },
  { name: 'Violet', hex: '#7C3AED' },
];

function colorHex(name: string): string {
  const trimmed = name.trim();
  if (/^#[0-9A-Fa-f]{3,8}$/.test(trimmed)) return trimmed;
  const found = CLOSET_COLORS.find((c) => c.name.toLowerCase() === trimmed.toLowerCase());
  return found ? found.hex : '#9CA3AF';
}

/**
 * Carrousel d'une partie du dressing : UN vêtement affiché au centre, avec des
 * flèches gauche/droite pour parcourir les autres vêtements de cette partie.
 */
export default function ClosetCarousel({ title, icon, items, emptyLabel, onOpen, onDelete, onQuickAdd }: ClosetCarouselProps) {
  const [index, setIndex] = useState(0);
  const total = items.length;

  // Si la liste change (ajout/suppression), on garde un index toujours valide.
  useEffect(() => {
    setIndex((prev) => (total === 0 ? 0 : Math.min(prev, total - 1)));
  }, [total]);

  const goPrev = () => setIndex((i) => (total === 0 ? 0 : (i - 1 + total) % total));
  const goNext = () => setIndex((i) => (total === 0 ? 0 : (i + 1) % total));

  const current = total > 0 ? items[Math.min(index, total - 1)] : null;
  const canSlide = total > 1;

  const arrowClasses =
    'w-10 h-10 rounded-full flex items-center justify-center border border-background-200/60 bg-background-50 text-foreground-600 cursor-pointer hover:bg-background-100 hover:text-foreground-900 transition-colors disabled:opacity-30 disabled:cursor-not-allowed';

  return (
    <section className="bg-background-50 border border-background-200/30 rounded-xl p-4 md:p-5">
      {/* En-tête de la partie */}
      <div className="flex items-center justify-between gap-2 mb-4">
        <h3 className="flex items-center gap-2 text-sm font-bold font-heading text-foreground-900">
          <span className="w-8 h-8 rounded-lg bg-foreground-100 text-foreground-700 flex items-center justify-center">
            <i className={`${icon} text-base`}></i>
          </span>
          {title}
        </h3>
        <div className="flex items-center gap-2">
          <span className="inline-flex items-center gap-1 text-xs font-semibold text-foreground-700 bg-foreground-100 rounded-full px-2.5 py-1" title={`${total} pièce${total !== 1 ? 's' : ''} dans ${title}`}>
            <i className="ri-shirt-line"></i>
            {total} pièce{total !== 1 ? 's' : ''}
          </span>
          {onQuickAdd && (
            <button
              type="button"
              onClick={onQuickAdd}
              title={`Ajouter une pièce dans « ${title} »`}
              aria-label={`Ajouter une pièce dans ${title}`}
              className="w-8 h-8 rounded-full bg-primary-500 text-background-50 flex items-center justify-center cursor-pointer hover:bg-primary-600 transition-colors"
            >
              <i className="ri-add-line"></i>
            </button>
          )}
        </div>
      </div>

      {current ? (
        <>
          <div className="flex items-center justify-center gap-3 sm:gap-6">
            <button type="button" onClick={goPrev} disabled={!canSlide} className={arrowClasses} aria-label="Vêtement précédent">
              <i className="ri-arrow-left-s-line text-xl"></i>
            </button>

            {/* Une image par carte, centrée */}
            <div className="w-40 sm:w-52 flex-shrink-0">
              <div
                className="relative aspect-[3/4] rounded-lg overflow-hidden bg-background-100 group cursor-pointer"
                onClick={() => onOpen(current)}
              >
                {current.photos && current.photos.length > 0 ? (
                  <img
                    src={current.photos[0]}
                    alt={current.name}
                    className="w-full h-full object-cover object-top"
                  />
                ) : (
                  <div className="w-full h-full flex flex-col items-center justify-center gap-2">
                    <i className={`${icon} text-3xl text-foreground-300`}></i>
                    <span className="text-[10px] text-foreground-400">Aucune photo</span>
                  </div>
                )}

                <span className="absolute top-2 left-2 px-2 py-0.5 bg-background-50/90 text-foreground-700 rounded-full text-[10px] font-medium">
                  {title}
                </span>

                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                  <div className="flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); onOpen(current); }}
                      className="w-9 h-9 rounded-full bg-background-50 text-foreground-700 flex items-center justify-center cursor-pointer hover:bg-background-100 shadow-sm"
                      aria-label="Modifier"
                    >
                      <i className="ri-edit-line text-sm"></i>
                    </button>
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); onDelete(current); }}
                      className="w-9 h-9 rounded-full bg-red-500 text-white flex items-center justify-center cursor-pointer hover:bg-red-600 shadow-sm"
                      aria-label="Supprimer"
                    >
                      <i className="ri-delete-bin-line text-sm"></i>
                    </button>
                  </div>
                </div>
              </div>

              <div className="mt-3 text-center">
                <p className="text-sm font-semibold text-foreground-800 truncate">{current.name}</p>
                {current.couleur && (
                  <div className="flex items-center justify-center gap-1 mt-1.5">
                    {current.couleur.split(',').map((c) => c.trim()).filter(Boolean).slice(0, 5).map((c, ci) => (
                      <span
                        key={`${c}-${ci}`}
                        className="w-3 h-3 rounded-full border border-background-300/60"
                        style={{ backgroundColor: colorHex(c) }}
                        title={c}
                      ></span>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <button type="button" onClick={goNext} disabled={!canSlide} className={arrowClasses} aria-label="Vêtement suivant">
              <i className="ri-arrow-right-s-line text-xl"></i>
            </button>
          </div>

          {/* Points de navigation */}
          {canSlide && (
            <div className="flex items-center justify-center gap-1.5 mt-4">
              {items.map((it, i) => (
                <button
                  key={it.id}
                  type="button"
                  onClick={() => setIndex(i)}
                  aria-label={`Voir ${it.name}`}
                  className={`rounded-full transition-all cursor-pointer ${
                    i === index ? 'w-4 h-1.5 bg-foreground-800' : 'w-1.5 h-1.5 bg-background-300 hover:bg-background-400'
                  }`}
                ></button>
              ))}
            </div>
          )}
        </>
      ) : (
        <div className="flex flex-col items-center justify-center py-8 text-center">
          <i className={`${icon} text-2xl text-foreground-300 mb-2`}></i>
          <p className="text-xs text-foreground-400 mb-3">{emptyLabel}</p>
          {onQuickAdd && (
            <button
              type="button"
              onClick={onQuickAdd}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-primary-500 text-background-50 text-xs font-semibold whitespace-nowrap cursor-pointer hover:bg-primary-600 transition-colors"
            >
              <i className="ri-add-line"></i>
              Ajouter dans « {title} »
            </button>
          )}
        </div>
      )}
    </section>
  );
}